import React, { Component } from 'react';
import { Modal, Grid, Row, Col, PageHeader, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './ViewInventory.css';
import { GetInventory } from '../libs/GraphQL';
import Loader from '../components/atoms/Loader';

const CLOUDINARY_URL = 'https://res.cloudinary.com/fftech/image/upload/';
const DEFAULT_ITEM_IMAGE = '/img/empty.png';

function stripTime(date) {
  const dateWithoutTime = new Date(date);
  dateWithoutTime.setHours(0, 0, 0, 0);
  return dateWithoutTime;
}

function toDateValue(dateObj) {
  if (!dateObj) return null;
  if (Array.isArray(dateObj)) {
    return new Date(dateObj[0], dateObj[1] - 1, dateObj[2]);
  }
  const parsedDate = new Date(dateObj);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate;
}

function getBestBeforeDateValue(expirationDateObj) {
  const parsed = toDateValue(expirationDateObj);
  if (!parsed) return null;
  // Add two days (in milliseconds) to the expiration date to calculate the "best before" date
  const bestBefore = new Date(parsed.getTime() + 2 * 24 * 60 * 60 * 1000);
  return bestBefore;
}

function isExpired(expirationDateObj) {
  const bestBefore = getBestBeforeDateValue(expirationDateObj);
  if (!bestBefore) return false;
  return stripTime(new Date()) >= stripTime(bestBefore);
}

function getVendItemName(slot) {
  const vendItemNames = new Set();
  slot.forEach((item) => vendItemNames.add(item.vendItemName));
  const itemNameInFront = vendItemNames.values().next().value;
  const vendItemName =
    vendItemNames.size > 1
      ? `${itemNameInFront} + ${vendItemNames.size - 1} more`
      : itemNameInFront || 'EMPTY_SLOT';
  return vendItemName;
}

function getFrontItem(slot) {
  if (!slot || !Array.isArray(slot)) return null;
  const sorted = slot
    .filter(Boolean)
    .map((item) => ({
      item,
      zIndex: item?.slot?.z ?? 0,
    }))
    .sort((a, b) => a.zIndex - b.zIndex);
  return sorted[0]?.item || null;
}

function isSlotBlocked(slot) {
  const frontItem = getFrontItem(slot);
  return frontItem ? isExpired(frontItem.expirationDate) : false;
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '';
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return '';

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 60 * 1000) {
    return 'Just now';
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (minutes) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);

  const label = parts.join(', ');
  return `${label} ago`;
}

// Date format YYYY-MM-DD
function formatYMD(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function handleBestBeforeDate(dateObj) {
  const bestBeforeDate = getBestBeforeDateValue(dateObj);
  if (!bestBeforeDate) return 'UNKNOWN';
  return `${formatYMD(bestBeforeDate)}`;
}

function handleDate(dateObj) {
  if (!dateObj) return 'UNKNOWN';
  if (Array.isArray(dateObj)) {
    const theDate = new Date(dateObj[0], dateObj[1] - 1, dateObj[2]);
    return `${formatYMD(theDate)}`;
  }
  // expecting string to convert from epoch to Date
  const theDate = new Date(dateObj);
  return `${formatYMD(theDate)}`;
}

export default class ViewInventoryV2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      timestamp: null,
      isLoading: true,
      inventory: [],
      modal: {
        show: false,
        item: {
          vendItemName: '',
          items: [],
        },
      },
      kiosk:
        window.location.pathname.split('/')[
          window.location.pathname.split('/').length - 1
        ],
    };
    this.showModal = this.showModal.bind(this);
  }

  async componentDidMount() {
    this.getInventory();
    document.addEventListener(
      'keydown',
      () => {
        this.setState((prevState) => ({
          modal: { ...prevState.modal, show: false },
        }));
      },
      false,
    );
  }

  async getInventory() {
    try {
      const inventoryResponse = await GetInventory({
        location: this.state.kiosk,
      });

      const timestamp = inventoryResponse?.getInventory?.timestamp;
      const inventoryArray =
        inventoryResponse?.getInventory?.itemsBySlot?.reduce((acc, item) => {
          const {
            slot: { x, y, z },
          } = item;
          acc[x] = acc[x] || Array(6).fill(null);
          acc[x][y] = acc[x][y] || [];
          acc[x][y][z] = item;
          return acc;
        }, []);

      const newState = {
        timestamp,
        isLoading: false,
        inventory: inventoryArray,
        kiosk: this.state.kiosk,
      };
      this.setState(newState);
    } catch (e) {
      console.error(e);
    }
  }

  showModal(slot) {
    const vendItemName = getVendItemName(slot);
    this.setState({
      modal: {
        show: true,
        item: {
          items: slot.slice().reverse(),
          vendItemName,
        },
      },
    });
  }

  renderSlotGrid() {
    const slots = [];

    this.state.inventory.forEach((row, xIndex) => {
      row.forEach((slot, yIndex) => {
        if (yIndex === 0) {
          const availableCount = row.reduce((slotSum, rowSlot) => {
            if (!rowSlot || !Array.isArray(rowSlot)) return slotSum;
            const sellableItems = rowSlot.filter(
              (item) => item && !isExpired(item.expirationDate),
            );
            return slotSum + sellableItems.length;
          }, 0);
          slots.push(
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${xIndex}-${yIndex}-slot-header`}
              className="inventory-slot-header-container"
            >
              <div
                className="inventory-slot-header"
                title={`${availableCount} total items available for sale in this row`}
              >
                Row {xIndex + 1}{' '}
                <span className="inventory-slot-header-count">
                  {availableCount}
                </span>
              </div>
            </div>,
          );
        }
        if (slot === null) {
          // eslint-disable-next-line react/no-array-index-key
          slots.push(<div key={`${xIndex}-${yIndex}-empty-slot`} />);
        } else {
          const slotBlocked = isSlotBlocked(slot);
          const vendItemName = getVendItemName(slot);
          const imageUrl = `${CLOUDINARY_URL}w_310,h_310,c_fill,f_webp/Products/${vendItemName}/BlockOut/image`;
          slots.push(
            <button
              type="button"
              // eslint-disable-next-line react/no-array-index-key
              key={`${xIndex}-${yIndex}`}
              className={`inventory-slot ${
                slotBlocked ? 'inventory-slot-blocked' : ''
              }`}
              onClick={() => this.showModal(slot)}
            >
              <img
                src={imageUrl}
                onError={(e) => {
                  e.target.src = DEFAULT_ITEM_IMAGE;
                }}
                alt={vendItemName}
                className="inventory-slot-img"
              />
              <div className="inventory-slot-name">{vendItemName}</div>
              <div className="inventory-slot-qty">{slot.length}</div>
              {slotBlocked && (
                <div className="inventory-slot-flag">Front item expired</div>
              )}
            </button>,
          );
        }
      });
    });
    return <div className="inventory-grid">{slots}</div>;
  }

  render() {
    const timestampLabel = formatTimestamp(this.state.timestamp);

    return (
      <span className="border border-primary">
        <Modal show={this.state.modal.show}>
          <Modal.Header>
            <Modal.Title>{this.state.modal.item.vendItemName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="updateModalBody">
            <div style={{ verticalAlign: 'middle', display: 'table-cell' }}>
              {this.state.modal.item.items.length === 0 && (
                <div> None in stock </div>
              )}
              {this.state.modal.item.items.length > 0 &&
                this.state.modal.item.items.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Row key={index}>
                    <Col xs={12}>
                      <div
                        className={`inventory-modal-item ${
                          isExpired(item.expirationDate)
                            ? 'inventory-modal-item-expired'
                            : ''
                        }`}
                      >
                        <div className="circle">
                          <strong>
                            {this.state.modal.item.items.length - index}
                          </strong>
                        </div>
                        <div>
                          {item.vendItemName}
                          <br />
                          <strong>Produced On: </strong>
                          {handleDate(item.productionDate)}
                          <br />
                          <strong>Best Before Date: </strong>
                          {handleBestBeforeDate(item.expirationDate)}
                          {isExpired(item.expirationDate) && (
                            <div className="inventory-item-status">
                              Expired - slot blocked until removed
                            </div>
                          )}
                        </div>
                      </div>
                      {index !== this.state.modal.item.items.length - 1 && (
                        <hr />
                      )}
                    </Col>
                  </Row>
                ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            &nbsp;
            <button
              type="button"
              className="btn modalButtons btn-warning"
              onClick={() => {
                this.setState((prevState) => ({
                  modal: { ...prevState.modal, show: false },
                }));
              }}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
        <Grid>
          <Row className="show-grid">
            <Col xs={12}>
              <PageHeader>
                {this.state.kiosk}
                {timestampLabel ? <small> as of {timestampLabel}</small> : null}
              </PageHeader>
              <div className="button-container">
                <Link to={`/locations/health/${this.state.kiosk}`}>
                  <Button bsStyle="primary" className="button-spacing">
                    Health
                  </Button>
                </Link>
                <Link to={`/locations/detail/${this.state.kiosk}`}>
                  <Button bsStyle="primary">Location Detail</Button>
                </Link>
              </div>
            </Col>
          </Row>
          {this.state.isLoading ? <Loader /> : this.renderSlotGrid()}
        </Grid>
      </span>
    );
  }
}
